import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../../../lib/api";
import {
  ArrowLeft, Save, Loader2, X, Plus, Search, Check, Eye, EyeOff, Send,
  CheckCircle2, AlertCircle, Calendar, MapPin, Info, Sparkles, FileText,
} from "lucide-react";
import PosterSection from "./PosterSection";

/**
 * Events V2 — Editor (Beta polish)
 *
 * Enhancements vs Sprint 1:
 *  - Skeleton screen while loading (no more raw spinner on a blank page)
 *  - Inline toast (success / error), replaces alert()
 *  - Sidebar with derived status preview, "hidden from public" badge and
 *    a coherent primary/secondary button hierarchy
 *  - Status options limited to what the DB actually accepts (draft/published
 *    /archived). "Programmato" surfaces automatically when start_at is in
 *    the future and cover_image is set.
 *  - Better validation feedback (title & start_at required, inline hints)
 *  - Improved cover uploader with drag-hover state
 */

// DB status enum lives here — MUST match Postgres CHECK constraint
// (draft | published | archived | cancelled | postponed).
const STATUS_OPTIONS = [
  { value: "draft",     label: "Bozza" },
  { value: "published", label: "Pubblicato" },
  { value: "archived",  label: "Archiviato" },
];

const HIDDEN_CUTOFF_YEAR = 2098;

const slugify = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "")
  .slice(0, 80);

const emptyEvent = {
  title: "", slug: "", slogan: "",
  short_description: "", description: "",
  start_at: "", end_at: "",
  location_name: "", address: "", city: "Sanluri", province: "SU", postal_code: "09025", country: "IT",
  cover_image: "",
  status: "draft",
  category_slug: "", category_label: "",
  organizers: [],
  // Locandina (V15)
  poster_url: null,
  poster_thumbnail_url: null,
  poster_original_filename: null,
  poster_mime_type: null,
  poster_size_bytes: null,
  poster_uploaded_at: null,
  poster_is_public: false,
  // AI Import editable fields (V16 + existing)
  organizer_name: "",
  contact_phone: "",
  contact_email: "",
  event_website: "",
  registration_url: "",
  registration_required: false,
  limited_seats: false,
  is_free: null,
  price_note: "",
};

const isFutureDate = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getTime() > Date.now(); } catch { return false; }
};

const isHiddenFromPublic = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getUTCFullYear() >= HIDDEN_CUTOFF_YEAR; } catch { return false; }
};

// Convert a Postgres-style ISO timestamp (with offset) to the shape expected by
// <input type="datetime-local"> which is `YYYY-MM-DDTHH:mm` (no seconds, no tz).
const toLocalInputValue = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

// ---------------------------------------------------------------------------
// Toast (mirrors DashboardV2)
// ---------------------------------------------------------------------------
const Toast = ({ toast, onDismiss }) => {
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(onDismiss, 3800);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  const tone = toast.tone === "error" ? "bg-rose-600 text-white" : "bg-slate-900 text-white";
  const Icon = toast.tone === "error" ? AlertCircle : CheckCircle2;
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70]"
      role="status"
      aria-live="polite"
      data-testid="editor-toast"
    >
      <div className={`flex items-center gap-3 rounded-full pl-4 pr-2 py-2 shadow-lg ${tone}`}>
        <Icon className="w-4 h-4 shrink-0" />
        <span className="text-sm">{toast.msg}</span>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 rounded-full hover:bg-white/10"
          aria-label="Chiudi"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reusable UI atoms
// ---------------------------------------------------------------------------
const Section = ({ title, subtitle, children, testId, icon: Icon }) => (
  <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={testId}>
    <header className="px-5 md:px-6 py-4 border-b border-slate-100">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </header>
    <div className="px-5 md:px-6 py-5">{children}</div>
  </section>
);

const Field = ({ label, hint, required, children, className = "", confidence }) => (
  <div className={className}>
    <div className="flex items-center justify-between mb-1.5">
      <label className="block text-xs font-medium text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {confidence && (
        <span
          className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest ring-1 ring-inset ${
            confidence === "high"   ? "bg-emerald-50 text-emerald-700 ring-emerald-200" :
            confidence === "medium" ? "bg-amber-50 text-amber-800 ring-amber-200" :
            confidence === "low"    ? "bg-rose-50 text-rose-700 ring-rose-200" :
            "bg-slate-100 text-slate-500 ring-slate-200"
          }`}
          title={`Confidenza AI ${confidence}`}
        >
          AI · {confidence}
        </span>
      )}
    </div>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition";

// ---------------------------------------------------------------------------
// Business autocomplete
// ---------------------------------------------------------------------------
const BusinessSelector = ({ selected, onChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/events-v2/businesses-autocomplete?q=${encodeURIComponent(query)}&limit=15`);
        setResults(r.data || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  const isSelected = (id) => selected.some((b) => b.id === id);
  const toggle = (biz) => {
    if (isSelected(biz.id)) onChange(selected.filter((b) => b.id !== biz.id));
    else onChange([...selected, biz]);
  };

  return (
    <div data-testid="business-selector">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selected.map((b) => (
            <span
              key={b.id}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-emerald-50 text-emerald-800 text-sm border border-emerald-200"
            >
              {b.name}
              <button
                type="button"
                onClick={() => toggle(b)}
                className="rounded-full p-0.5 hover:bg-emerald-100 transition"
                data-testid={`biz-chip-remove-${b.id}`}
                aria-label={`Rimuovi ${b.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-slate-300 text-sm text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition"
          data-testid="biz-picker-open"
        >
          <Plus className="w-4 h-4" /> Aggiungi attività
        </button>
      ) : (
        <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca attività per nome…"
              className={`${inputCls} pl-9`}
              data-testid="biz-picker-search"
            />
          </div>
          <div className="max-h-64 overflow-auto rounded-lg bg-white border border-slate-100">
            {loading ? (
              <div className="p-4 text-center text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Ricerca…
              </div>
            ) : results.length === 0 ? (
              <div className="p-4 text-center text-slate-400 text-sm">Nessuna attività trovata</div>
            ) : results.map((biz) => (
              <button
                key={biz.id}
                type="button"
                onClick={() => toggle(biz)}
                data-testid={`biz-picker-result-${biz.id}`}
                className="w-full flex items-center gap-3 p-2.5 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0 transition"
              >
                {biz.logo_url ? (
                  <img src={biz.logo_url} alt="" loading="lazy" className="w-8 h-8 rounded-md object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-md bg-slate-100" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{biz.name}</div>
                  <div className="text-xs text-slate-500 truncate">{biz.category?.name || "—"}</div>
                </div>
                {isSelected(biz.id) && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
              </button>
            ))}
          </div>
          <div className="text-right mt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-slate-500 hover:text-slate-800"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cover uploader
// ---------------------------------------------------------------------------
const CoverUploader = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.public_url || r.data.url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div data-testid="cover-uploader">
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-slate-200 group">
          <img src={value} alt="Copertina" className="w-full aspect-[16/9] object-cover" />
          <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/20 transition pointer-events-none" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-3 right-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium text-slate-700 shadow-sm hover:bg-white transition"
            data-testid="cover-remove"
          >
            Rimuovi
          </button>
        </div>
      ) : (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`block cursor-pointer border-2 border-dashed rounded-xl p-10 text-center transition ${
            dragging
              ? "border-emerald-500 bg-emerald-50/40"
              : "border-slate-200 hover:border-emerald-400 hover:bg-slate-50/50"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            disabled={uploading}
            data-testid="cover-file-input"
          />
          {uploading ? (
            <span className="text-sm text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
            </span>
          ) : (
            <>
              <div className="text-sm font-medium text-slate-700">Trascina un&apos;immagine o clicca per caricare</div>
              <div className="text-xs text-slate-400 mt-1">JPG · PNG · WebP — max 8 MB · rapporto 16:9 consigliato</div>
            </>
          )}
        </label>
      )}
      {error && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-600">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Editor skeleton
// ---------------------------------------------------------------------------
const EditorSkeleton = () => (
  <div className="min-h-screen bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 animate-pulse">
      <div className="h-4 w-32 bg-slate-100 rounded" />
      <div className="mt-6 h-8 w-72 bg-slate-100 rounded" />
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
              <div className="h-4 w-40 bg-slate-100 rounded" />
              <div className="h-9 bg-slate-100 rounded-lg" />
              <div className="h-9 bg-slate-100 rounded-lg" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3 h-fit">
          <div className="h-3 w-16 bg-slate-100 rounded" />
          <div className="h-9 bg-slate-100 rounded-lg" />
          <div className="h-10 bg-slate-100 rounded-full" />
          <div className="h-10 bg-slate-100 rounded-full" />
        </div>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Editor
// ---------------------------------------------------------------------------
export default function EventEditorV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const importAuditId = isNew ? searchParams.get("import") : null;

  const [ev, setEv] = useState(emptyEvent);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState(null);
  const [validationErr, setValidationErr] = useState(null);
  const [importMeta, setImportMeta] = useState(null); // {confidence,warnings,missing_fields,provider}

  // Hydrate a new event from an AI import (sessionStorage payload).
  useEffect(() => {
    if (!isNew || !importAuditId) return;
    let payload = null;
    try {
      const raw = window.sessionStorage.getItem(`ai_import_${importAuditId}`);
      payload = raw ? JSON.parse(raw) : null;
    } catch { payload = null; }
    if (!payload || !payload.extracted) return;

    const ex = payload.extracted || {};
    const useAsCover = payload.use_poster_as_cover !== false; // default true
    // Build ISO datetime-local strings from date+time pairs
    const buildLocal = (d, t) => {
      if (!d) return "";
      return `${d}T${t || "00:00"}`;
    };
    const posterMeta = payload.poster_meta || {};
    setEv((prev) => ({
      ...prev,
      title: ex.title || "",
      slogan: ex.subtitle || "",
      short_description: ex.short_description || "",
      description: ex.full_description || "",
      start_at: buildLocal(ex.start_date, ex.start_time),
      end_at: buildLocal(ex.end_date, ex.end_time),
      location_name: ex.venue_name || "",
      address: ex.address || "",
      city: ex.city || prev.city,
      // Locandina — sempre valorizzata
      poster_url: payload.poster_url || null,
      poster_thumbnail_url: payload.poster_thumbnail_url || null,
      poster_original_filename: posterMeta.original_filename || null,
      poster_mime_type: posterMeta.mime_type || null,
      poster_size_bytes: posterMeta.size_bytes || null,
      poster_uploaded_at: posterMeta.uploaded_at || null,
      poster_is_public: posterMeta.is_public !== false, // default true per spec
      // Cover — solo se richiesto dall'utente in fase di import
      cover_image: useAsCover ? (payload.poster_thumbnail_url || payload.poster_url || "") : "",
      status: "draft",
      // Campi AI ora editabili (Sprint 2.3 · Patch V16 + colonne esistenti)
      organizer_name: ex.organizer || "",
      contact_phone: ex.phone || "",
      contact_email: ex.email || "",
      event_website: ex.website || "",
      registration_url: ex.booking_url || "",
      registration_required: ex.booking_required ?? false,
      limited_seats: ex.limited_seats ?? false,
      is_free: ex.is_free ?? null,
      price_note: ex.price_text || "",
      category_slug: ex.category ? ex.category.toLowerCase().replace(/\s+/g, "-") : (prev.category_slug || ""),
      category_label: ex.category || prev.category_label,
    }));
    setImportMeta({
      confidence: ex.confidence || {},
      warnings: ex.warnings || [],
      missing_fields: ex.missing_fields || [],
      provider: payload.provider,
    });
    setToast({ tone: "success", msg: `Scheda precompilata dall'AI (${payload.provider || "AI"})` });
  }, [isNew, importAuditId]);

  // Load existing event
  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [eventRes, bizRes] = await Promise.all([
          api.get(`/admin/events/${id}`).then((r) => r.data),
          api.get(`/admin/events/${id}/businesses`).then((r) => r.data).catch(() => []),
        ]);
        if (cancelled) return;
        setEv({
          ...emptyEvent,
          ...eventRes,
          start_at: toLocalInputValue(eventRes.start_at),
          end_at: toLocalInputValue(eventRes.end_at),
        });
        setBusinesses(bizRes.map((r) => r.business).filter(Boolean));
      } catch (e) {
        if (!cancelled) setToast({ tone: "error", msg: e?.response?.data?.detail || "Errore nel caricamento" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  const set = useCallback((k, v) => setEv((prev) => ({ ...prev, [k]: v })), []);

  // Auto-slug from title when empty
  useEffect(() => {
    if (isNew && ev.title && !ev.slug) set("slug", slugify(ev.title));
  }, [ev.title, ev.slug, isNew, set]);

  const canSave = ev.title?.trim() && ev.start_at;
  const hidden = isHiddenFromPublic(ev.visible_from);
  const conf = (k) => importMeta?.confidence?.[k];

  // Compute the "visual" status shown next to the badges
  const displayStatus = useMemo(() => {
    if (ev.status === "draft" && ev.cover_image && isFutureDate(ev.start_at)) return "programmato";
    return ev.status || "draft";
  }, [ev.status, ev.cover_image, ev.start_at]);

  const statusMeta = {
    draft:       { label: "Bozza",       chip: "bg-slate-100 text-slate-700" },
    programmato: { label: "Programmato", chip: "bg-amber-50 text-amber-800" },
    published:   { label: "Pubblicato",  chip: "bg-emerald-50 text-emerald-800" },
    archived:    { label: "Archiviato",  chip: "bg-zinc-100 text-zinc-600" },
    cancelled:   { label: "Annullato",   chip: "bg-rose-50 text-rose-700" },
    postponed:   { label: "Rinviato",    chip: "bg-indigo-50 text-indigo-700" },
  }[displayStatus] || { label: displayStatus, chip: "bg-slate-100 text-slate-700" };

  const saveEvent = async ({ publish = false } = {}) => {
    if (!canSave) {
      setValidationErr("Compila almeno Titolo e Data e ora inizio.");
      setToast({ tone: "error", msg: "Compila almeno Titolo e Data inizio" });
      return;
    }
    setValidationErr(null);
    if (publish) setPublishing(true); else setSaving(true);
    try {
      const payload = {
        title: ev.title, slug: ev.slug || slugify(ev.title),
        slogan: ev.slogan || null,
        short_description: ev.short_description || null,
        description: ev.description || null,
        start_at: ev.start_at, end_at: ev.end_at || null,
        location_name: ev.location_name || null,
        address: ev.address || null,
        city: ev.city || null, province: ev.province || null,
        postal_code: ev.postal_code || null, country: ev.country || "IT",
        cover_image: ev.cover_image || null,
        status: publish ? "published" : (ev.status || "draft"),
        // Locandina (V15) — inviate solo se presenti nel local state.
        poster_url: ev.poster_url || null,
        poster_thumbnail_url: ev.poster_thumbnail_url || null,
        poster_original_filename: ev.poster_original_filename || null,
        poster_mime_type: ev.poster_mime_type || null,
        poster_size_bytes: ev.poster_size_bytes || null,
        poster_uploaded_at: ev.poster_uploaded_at || null,
        poster_is_public: !!ev.poster_is_public,
        // AI editable fields (V16 + existing columns)
        organizer_name: ev.organizer_name || null,
        contact_phone: ev.contact_phone || null,
        contact_email: ev.contact_email || null,
        event_website: ev.event_website || null,
        registration_url: ev.registration_url || null,
        registration_required: !!ev.registration_required,
        limited_seats: !!ev.limited_seats,
        is_free: ev.is_free,
        price_note: ev.price_note || null,
        category_slug: ev.category_slug || null,
        category_label: ev.category_label || null,
      };

      let eventId = id;
      if (isNew) {
        const r = await api.post("/admin/events", payload);
        eventId = r.data.id;
      } else {
        await api.patch(`/admin/events/${id}`, payload);
      }

      // Sync businesses (Sprint 1: single "participant" role). Non-blocking:
      // if the V14 table is missing the backend returns {skipped:true} and
      // the save toast still succeeds.
      try {
        await api.put(`/admin/events/${eventId}/businesses`, {
          business_ids: businesses.map((b) => b.id),
          role: "participant",
        });
      } catch (bizErr) {
        // Log but do not block the save flow
        console.warn("[EditorV2] businesses sync skipped:", bizErr?.response?.data || bizErr.message);
      }

      setToast({ tone: "success", msg: publish ? "Evento pubblicato" : "Modifiche salvate" });
      if (isNew) navigate(`/admin/eventi-v2/${eventId}`);
      else setEv((prev) => ({ ...prev, status: payload.status, updated_at: new Date().toISOString() }));
    } catch (e) {
      setToast({ tone: "error", msg: e?.response?.data?.detail || "Errore durante il salvataggio" });
    } finally { setSaving(false); setPublishing(false); }
  };

  if (loading) return <EditorSkeleton />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="event-editor-v2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <Link
          to="/admin/eventi-v2"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition"
          data-testid="back-to-events"
        >
          <ArrowLeft className="w-4 h-4" /> Eventi
        </Link>

        {/* Header */}
        <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {isNew ? "Nuovo evento" : "Modifica evento"}
            </div>
            <h1 className="mt-1 text-2xl md:text-4xl font-semibold text-slate-900 tracking-tight break-words">
              {ev.title || <span className="text-slate-400">Senza titolo</span>}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusMeta.chip}`}>
                {statusMeta.label}
              </span>
              {hidden && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-2.5 py-1 text-[11px] font-semibold">
                  <EyeOff className="w-3 h-3" /> Nascosto al pubblico
                </span>
              )}
              {ev.featured && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-amber-900 px-2.5 py-1 text-[11px] font-semibold">
                  ★ In evidenza
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          {/* MAIN COLUMN */}
          <div className="space-y-5">
            {importMeta && (
              <div
                className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 sm:p-5"
                data-testid="ai-import-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-emerald-900">
                      Scheda precompilata dall&apos;AI
                    </div>
                    <div className="mt-0.5 text-xs text-emerald-800/80">
                      Locandina analizzata da <b>{importMeta.provider}</b>. Controlla i campi qui sotto
                      e correggi ciò che serve prima di salvare la bozza.
                    </div>
                    {importMeta.missing_fields?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-[11px] uppercase tracking-widest text-emerald-800/70 font-semibold mb-1.5">
                          Da completare a mano ({importMeta.missing_fields.length})
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {importMeta.missing_fields.map((f) => (
                            <span
                              key={f}
                              className="inline-flex items-center rounded-full bg-white ring-1 ring-emerald-200 text-emerald-900 px-2.5 py-0.5 text-[11px]"
                            >
                              {f}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {importMeta.warnings?.length > 0 && (
                      <details className="mt-3 group">
                        <summary className="cursor-pointer text-[11px] uppercase tracking-widest text-amber-800/80 font-semibold select-none inline-flex items-center gap-1">
                          Note dell&apos;analisi ({importMeta.warnings.length})
                          <span className="group-open:rotate-180 transition inline-block">▾</span>
                        </summary>
                        <ul className="mt-1.5 text-xs text-amber-900 space-y-0.5 list-disc pl-4">
                          {importMeta.warnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}
            <Section title="Informazioni principali" icon={Info} testId="sec-info">
              <div className="space-y-4">
                <Field label="Titolo" required confidence={conf("title")}>
                  <input
                    className={inputCls}
                    value={ev.title}
                    onChange={(e) => set("title", e.target.value)}
                    data-testid="field-title"
                    placeholder="Es. Sagra del Pane 2026"
                  />
                </Field>
                <Field label="Sottotitolo / Slogan" confidence={conf("subtitle")}>
                  <input
                    className={inputCls}
                    value={ev.slogan || ""}
                    onChange={(e) => set("slogan", e.target.value)}
                    data-testid="field-slogan"
                    placeholder="Un weekend tra profumi e tradizione"
                  />
                </Field>
                <Field
                  label="Descrizione breve"
                  hint="Frase d'apertura mostrata nelle card e nell'anteprima social. Consigliati max 200 caratteri."
                  confidence={conf("short_description")}
                >
                  <textarea
                    rows={2}
                    className={inputCls}
                    value={ev.short_description || ""}
                    onChange={(e) => set("short_description", e.target.value)}
                    data-testid="field-short-desc"
                  />
                </Field>
                <Field
                  label="Descrizione completa"
                  hint="Testo esteso mostrato nella pagina dell'evento."
                  confidence={conf("full_description")}
                >
                  <textarea
                    rows={6}
                    className={inputCls}
                    value={ev.description || ""}
                    onChange={(e) => set("description", e.target.value)}
                    data-testid="field-full-desc"
                  />
                </Field>

                <details className="rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-2">
                  <summary
                    className="cursor-pointer text-xs font-medium text-slate-600 py-1 select-none"
                    data-testid="advanced-toggle"
                  >
                    Impostazioni avanzate (slug URL)
                  </summary>
                  <div className="pt-3 pb-1">
                    <Field
                      label="Slug URL"
                      hint="Generato automaticamente dal titolo. Modificalo solo se hai un motivo SEO specifico."
                    >
                      <input
                        className={inputCls}
                        value={ev.slug || ""}
                        onChange={(e) => set("slug", slugify(e.target.value))}
                        data-testid="field-slug"
                      />
                    </Field>
                  </div>
                </details>
              </div>
            </Section>

            <Section title="Data e orari" icon={Calendar} testId="sec-date">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Data e ora inizio" required confidence={conf("start_date") || conf("start_time")}>
                  <input
                    type="datetime-local"
                    className={inputCls}
                    value={ev.start_at || ""}
                    onChange={(e) => set("start_at", e.target.value)}
                    data-testid="field-start"
                  />
                </Field>
                <Field label="Data e ora fine" hint="Facoltativa" confidence={conf("end_date") || conf("end_time")}>
                  <input
                    type="datetime-local"
                    className={inputCls}
                    value={ev.end_at || ""}
                    onChange={(e) => set("end_at", e.target.value)}
                    data-testid="field-end"
                  />
                </Field>
              </div>
            </Section>

            <Section title="Luogo" icon={MapPin} testId="sec-location">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nome luogo" confidence={conf("venue_name")}>
                  <input
                    className={inputCls}
                    value={ev.location_name || ""}
                    onChange={(e) => set("location_name", e.target.value)}
                    data-testid="field-location-name"
                    placeholder="Es. Piazza Castello"
                  />
                </Field>
                <Field label="Indirizzo" confidence={conf("address")}>
                  <input
                    className={inputCls}
                    value={ev.address || ""}
                    onChange={(e) => set("address", e.target.value)}
                    data-testid="field-address"
                    placeholder="Via, numero civico, CAP…"
                  />
                </Field>
              </div>
            </Section>

            <Section
              title="Contatti e organizzazione"
              subtitle="Chi organizza l'evento e a chi si possono chiedere informazioni. Campi opzionali."
              icon={Info}
              testId="sec-contacts"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Organizzatore" confidence={conf("organizer")}>
                  <input
                    className={inputCls}
                    value={ev.organizer_name || ""}
                    onChange={(e) => set("organizer_name", e.target.value)}
                    data-testid="field-organizer"
                    placeholder="Es. Pro Loco Sanluri"
                  />
                </Field>
                <Field label="Categoria" confidence={conf("category")}>
                  <input
                    className={inputCls}
                    value={ev.category_label || ""}
                    onChange={(e) => {
                      const label = e.target.value;
                      set("category_label", label);
                      set("category_slug", label.toLowerCase().replace(/\s+/g, "-"));
                    }}
                    data-testid="field-category"
                    placeholder="Es. Concerto, Mercatino, Sagra…"
                  />
                </Field>
                <Field label="Telefono" confidence={conf("phone")}>
                  <input
                    type="tel"
                    className={inputCls}
                    value={ev.contact_phone || ""}
                    onChange={(e) => set("contact_phone", e.target.value)}
                    data-testid="field-phone"
                    placeholder="070 123 4567"
                  />
                </Field>
                <Field label="Email" confidence={conf("email")}>
                  <input
                    type="email"
                    className={inputCls}
                    value={ev.contact_email || ""}
                    onChange={(e) => set("contact_email", e.target.value)}
                    data-testid="field-email"
                    placeholder="info@…"
                  />
                </Field>
                <Field label="Sito web" confidence={conf("website")}>
                  <input
                    type="url"
                    className={inputCls}
                    value={ev.event_website || ""}
                    onChange={(e) => set("event_website", e.target.value)}
                    data-testid="field-website"
                    placeholder="https://…"
                  />
                </Field>
                <Field label="Link prenotazione" confidence={conf("booking_url")}>
                  <input
                    type="url"
                    className={inputCls}
                    value={ev.registration_url || ""}
                    onChange={(e) => set("registration_url", e.target.value)}
                    data-testid="field-booking-url"
                    placeholder="https://…"
                  />
                </Field>
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Prezzo (testo libero)" hint="Es. €8 · bambini gratis. Lascialo vuoto se ingresso libero.">
                  <input
                    className={inputCls}
                    value={ev.price_note || ""}
                    onChange={(e) => set("price_note", e.target.value)}
                    data-testid="field-price-note"
                    placeholder="Es. €5 · gratuito fino a 12 anni"
                  />
                </Field>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ev.is_free === true}
                    onChange={(e) => set("is_free", e.target.checked ? true : (ev.price_note ? false : null))}
                    className="mt-0.5 accent-emerald-600"
                    data-testid="field-is-free"
                  />
                  <div className="text-xs text-slate-800">
                    <div className="font-medium">Ingresso libero</div>
                    <div className="text-slate-500">Gratuito, nessun biglietto</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!ev.registration_required}
                    onChange={(e) => set("registration_required", e.target.checked)}
                    className="mt-0.5 accent-emerald-600"
                    data-testid="field-registration-required"
                  />
                  <div className="text-xs text-slate-800">
                    <div className="font-medium">Su prenotazione</div>
                    <div className="text-slate-500">Richiesta iscrizione</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!ev.limited_seats}
                    onChange={(e) => set("limited_seats", e.target.checked)}
                    className="mt-0.5 accent-emerald-600"
                    data-testid="field-limited-seats"
                  />
                  <div className="text-xs text-slate-800">
                    <div className="font-medium">Posti limitati</div>
                    <div className="text-slate-500">Capienza ristretta</div>
                  </div>
                </label>
              </div>
            </Section>

            <Section
              title="Locandina"
              subtitle="Documento originale dell'evento, disponibile per la visualizzazione e il download."
              icon={FileText}
              testId="sec-poster"
            >
              <PosterSection
                value={{
                  url: ev.poster_url,
                  thumbnail_url: ev.poster_thumbnail_url,
                  filename: ev.poster_original_filename,
                  mime_type: ev.poster_mime_type,
                  size_bytes: ev.poster_size_bytes,
                  uploaded_at: ev.poster_uploaded_at,
                  is_public: !!ev.poster_is_public,
                }}
                eventId={isNew ? null : id}
                onChange={(patch) => setEv((prev) => ({ ...prev, ...patch }))}
                onError={(m) => setToast({ tone: "error", msg: m })}
                onSuccess={(m) => setToast({ tone: "success", msg: m })}
              />
            </Section>

            <Section
              title="Immagine di copertina"
              subtitle="Verrà utilizzata come cover della pagina evento e nell'anteprima social."
              testId="sec-cover"
            >
              <CoverUploader value={ev.cover_image} onChange={(v) => set("cover_image", v)} />
            </Section>

            <Section
              title="Attività coinvolte"
              subtitle="Attività del CCN associate all'evento. Compariranno nella sezione 'Attività aderenti' della pagina pubblica."
              testId="sec-businesses"
            >
              <BusinessSelector selected={businesses} onChange={setBusinesses} />
            </Section>
          </div>

          {/* STICKY SIDEBAR */}
          <aside className="lg:sticky lg:top-8 lg:self-start space-y-4" data-testid="editor-sidebar">
            {/* Publish card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                Stato dell&apos;evento
              </div>
              <select
                value={ev.status || "draft"}
                onChange={(e) => set("status", e.target.value)}
                className={`${inputCls} mb-1`}
                data-testid="field-status"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {displayStatus === "programmato" && (
                <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 rounded-md px-2 py-1.5 leading-snug">
                  Bozza già completata di locandina e data futura — verrà mostrata come <b>Programmato</b>.
                </div>
              )}

              <div className="mt-4 grid gap-2">
                <button
                  type="button"
                  onClick={() => saveEvent()}
                  disabled={saving || publishing || !canSave}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  data-testid="save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salva
                </button>
                <button
                  type="button"
                  onClick={() => saveEvent({ publish: true })}
                  disabled={saving || publishing || !canSave || ev.status === "published"}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-2.5 font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  data-testid="publish-btn"
                >
                  {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {ev.status === "published" ? "Pubblicato" : "Pubblica ora"}
                </button>
              </div>

              {!canSave && (
                <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 rounded-md px-2.5 py-2 leading-snug">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                  Per abilitare Salva/Pubblica servono <b>Titolo</b> e <b>Data inizio</b>.
                </div>
              )}
              {validationErr && (
                <div className="mt-3 text-[11px] text-rose-700 bg-rose-50 rounded-md px-2.5 py-2">
                  {validationErr}
                </div>
              )}

              {ev.updated_at && (
                <div className="mt-4 text-[11px] text-slate-400 border-t border-slate-100 pt-3">
                  Ultima modifica: <span className="text-slate-600">{new Date(ev.updated_at).toLocaleString("it-IT")}</span>
                </div>
              )}
            </div>

            {/* Visibility card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                Visibilità pubblica
              </div>
              {hidden ? (
                <div className="flex items-start gap-2 text-xs text-slate-600 leading-snug">
                  <EyeOff className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
                  <div>
                    L&apos;evento è <b>nascosto</b> al sito pubblico (data di visibilità futura).
                    Per farlo comparire ai visitatori azzera il campo <code>visible_from</code>
                    dal DB — sarà gestibile qui nel prossimo sprint.
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 text-xs text-slate-600 leading-snug">
                  <Eye className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
                  <div>
                    Quando lo stato è <b>Pubblicato</b>, l&apos;evento è visibile sul sito
                    <span className="text-slate-400"> (sanluriccn.it/eventi)</span>.
                  </div>
                </div>
              )}
            </div>

            {!isNew && ev.slug && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 text-xs text-slate-500">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
                  URL pubblico
                </div>
                <span className="break-all text-slate-700 font-mono text-[11px]">/eventi/{ev.slug}</span>
              </div>
            )}
          </aside>
        </div>
      </div>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
